import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useThrottleFn } from '@vueuse/core';
import useResizeObserver from '@/hooks/resizeObserver';

export function useVirtualList(props) {
  // 状态管理
  const rowHeights = ref({});
  const offsetCache = ref({});
  const startIndex = ref(0);
  const endIndex = ref(0);
  const rowRefsByIndex = ref([]);
  const rowRefsByKey = ref({});

  // 解构虚拟列表配置
  const { enabled, overscan, estimateHeight, threshold } = props.virtual;

  // 数据源
  const tableData = computed(() => props.tableData.value);

  const dataLength = computed(() => tableData.value.length);

  // 可见行数据
  const visibleRows = computed(() => {
    if (!enabled || dataLength.value <= threshold) {
      // 非虚拟列表模式：返回所有真实数据，但要做空值检查
      return (tableData.value || []).map((item, index) => ({
        data: item,
        originalIndex: index,
        isPlaceholder: false,
        key: item && item[props.rowKey] ? item[props.rowKey] : `row-${index}`
      }));
    }

    // 虚拟列表模式：只生成可见范围内的行数据，不生成所有占位符
    const result = [];
    const start = Math.max(0, startIndex.value);
    const end = Math.min(dataLength.value, endIndex.value);

    for (let i = start; i < end; i++) {
      const rowData = tableData.value[i];
      if (rowData) {
        // 只生成可见范围内的真实数据行
        result.push({
          data: rowData,
          originalIndex: i,
          isPlaceholder: false,
          key: rowData[props.rowKey] || `row-${i}`
        });
      }
    }
    return result;
  });

  // 工具函数
  const getRowHeight = (index) => {
    return rowHeights.value[index] || estimateHeight;
  };

  const measureRowHeight = (index, height) => {
    if (!height || height <= 0) return;

    const oldHeight = rowHeights.value[index];
    if (!oldHeight || Math.abs(oldHeight - height) > 1) {
      rowHeights.value[index] = height;
      clearOffsetCache();
    }
  };

  const clearOffsetCache = () => {
    offsetCache.value = {};
  };

  const getRowOffset = (index) => {
    if (offsetCache.value[index] !== undefined) {
      return offsetCache.value[index];
    }

    let offset = 0;
    for (let i = 0; i < index; i++) {
      offset += getRowHeight(i);
    }
    offsetCache.value[index] = offset;
    return offset;
  };

  // 更新可见范围
  const updateVisibleRange = useThrottleFn((scrollTop, containerHeight) => {
    if (!enabled || !dataLength.value || !containerHeight) return;
    let accumulatedHeight = 0;
    let newStartIndex = 0;

    // 计算起始索引
    for (let i = 0; i < dataLength.value; i++) {
      const rowHeight = getRowHeight(i);
      if (accumulatedHeight + rowHeight > scrollTop) {
        newStartIndex = Math.max(0, i - overscan);
        break;
      }
      accumulatedHeight += rowHeight;
    }

    // 计算结束索引
    let visibleHeight = 0;
    let newEndIndex = newStartIndex;

    for (let i = newStartIndex; i < dataLength.value; i++) {
      visibleHeight += getRowHeight(i);
      if (visibleHeight > containerHeight + estimateHeight * overscan) {
        newEndIndex = i;
        break;
      }
      if (i === dataLength.value - 1) {
        newEndIndex = dataLength.value;
      }
    }

    newEndIndex = Math.min(dataLength.value, newEndIndex + overscan);

    if (startIndex.value !== newStartIndex || endIndex.value !== newEndIndex) {
      startIndex.value = newStartIndex;
      endIndex.value = newEndIndex;
    }
  }, 16);

  // 行元素引用管理
  const setRowRef = (el, index) => {
    if (!enabled) return;

    if (!el) {
      rowRefsByIndex.value[index] = null;
      const rowData = visibleRows.value[index];
      if (rowData && !rowData.isPlaceholder && rowData.data) {
        const rowKey = rowData.data[props.rowKey];
        if (rowKey) {
          rowRefsByKey.value[rowKey] = null;
        }
      }
      return;
    }

    rowRefsByIndex.value[index] = el;
    const rowData = visibleRows.value[index];
    if (rowData && !rowData.isPlaceholder && rowData.data) {
      const rowKey = rowData.data[props.rowKey];
      if (rowKey) {
        rowRefsByKey.value[rowKey] = { element: el, index };
      }
    }

    // 测量行高
    if (rowData && !rowData.isPlaceholder) {
      nextTick(() => {
        if (el.offsetHeight > 0) {
          measureRowHeight(index, el.offsetHeight);
        }
      });
    }
  };

  const findRowByKey = (rowKey) => {
    if (!enabled) return null;
    return rowRefsByKey.value[rowKey] || null;
  };

  // 行样式计算
  const getRowStyle = (row) => {
    if (!enabled) return {};
    return {
      top: `${getRowOffset(row.originalIndex)}px`
      // height: `${getRowHeight(row.originalIndex)}px`,
    };
  };

  // 数据变化监听
  watch(dataLength, (v) => {
    if (!enabled) return;
    // 数据长度变化时清理无效缓存
    const newHeights = {};
    Object.keys(rowHeights.value).forEach((key) => {
      const index = parseInt(key);
      if (index < v) newHeights[key] = rowHeights.value[key];
    });
    rowHeights.value = newHeights;
    clearOffsetCache();

    // 重新初始化范围
    if (v > 0) {
      const initialEndIndex = Math.min(v, 20);
      startIndex.value = 0;
      endIndex.value = initialEndIndex;
    }
  });

  // ResizeObserver 相关（保持原有逻辑）
  const { observeElements, unobserveElements, disconnectObserver } = useResizeObserver();

  const handleResize = (entry) => {
    const target = entry.target;
    const index = rowRefsByIndex.value.findIndex((ref) => ref === target);
    if (index >= 0 && index < dataLength.value) {
      measureRowHeight(index, entry.contentRect.height);
    }
  };

  // 可见行变化监听（优化：减少不必要的观察和取消观察）
  let prevObservedRefs = new Set();
  watch(visibleRows, () => {
    if (!enabled) return;

    nextTick(() => {
      // 获取当前可见的真实行引用
      const currentRealRefs = [];
      for (let i = startIndex.value; i < endIndex.value; i++) {
        if (rowRefsByIndex.value[i]) {
          currentRealRefs.push(rowRefsByIndex.value[i]);
        }
      }

      // 找出需要取消观察的引用
      const toUnobserve = [];
      prevObservedRefs.forEach((ref) => {
        if (!currentRealRefs.includes(ref)) {
          toUnobserve.push(ref);
        }
      });

      // 找出需要新增观察的引用
      const toObserve = [];
      currentRealRefs.forEach((ref) => {
        if (!prevObservedRefs.has(ref)) {
          toObserve.push(ref);
        }
      });

      // 执行观察和取消观察操作
      if (toUnobserve.length > 0) {
        unobserveElements(toUnobserve);
      }
      if (toObserve.length > 0) {
        observeElements(toObserve, handleResize);
      }

      // 更新已观察引用集合
      prevObservedRefs = new Set(currentRealRefs);
    });
  });

  const allHeight = computed(() => {
    if (!enabled) return 0;
    const lastIndex = dataLength.value - 1;
    return getRowOffset(lastIndex) + getRowHeight(lastIndex);
  });

  onMounted(() => {
    if (!enabled) return;

    nextTick(() => {
      // 获取初始可见的真实行引用
      const currentRealRefs = [];
      for (let i = startIndex.value; i < endIndex.value; i++) {
        if (rowRefsByIndex.value[i]) {
          currentRealRefs.push(rowRefsByIndex.value[i]);
        }
      }

      // 观察初始可见行
      observeElements(currentRealRefs, handleResize);
      prevObservedRefs = new Set(currentRealRefs);
    });
  });

  onBeforeUnmount(() => {
    // 清理 ResizeObserver
    disconnectObserver();

    // 清理所有引用和缓存
    prevObservedRefs.clear();
    visibleRows.value = [];
    rowRefsByIndex.value = [];
    rowRefsByKey.value = {};
    rowHeights.value = {};
    offsetCache.value = {};
  });

  return {
    visibleRows,
    getRowStyle,
    setRowRef,
    findRowByKey,
    updateVisibleRange,
    measureRowHeight,
    getRowHeight,
    allHeight
  };
}
